import React, { useState, useEffect } from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { TrendingUp, TrendingDown, Download, Banknote, Users, Mail, Send } from 'lucide-react';
import { toast } from '../../../hooks/use-toast';
import api from '../../../utils/api';
import { Switch } from '../../../components/ui/switch';
import { Input } from '../../../components/ui/input';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid
} from 'recharts';

const monthStart = (offset = 0) => {
  const n = new Date();
  const d = new Date(n.getFullYear(), n.getMonth() + offset, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const monthEnd = (offset = 0) => {
  const n = new Date();
  const d = new Date(n.getFullYear(), n.getMonth() + offset + 1, 0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const todayStr = () => new Date().toISOString().slice(0, 10);

const StatCard = ({ label, value, accent, testid, sub }) => (
  <Card className="p-4">
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`text-2xl font-bold ${accent || 'text-gray-900'}`} data-testid={testid}>{value}</p>
    {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
  </Card>
);

const PlatformRevenueTab = () => {
  const [range, setRange] = useState({ from: monthStart(), to: todayStr() });
  const [daily, setDaily] = useState(null);
  const [byOperator, setByOperator] = useState(null);
  const [prevFees, setPrevFees] = useState(null);
  const [loading, setLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [emailCfg, setEmailCfg] = useState({ enabled: true, recipients: '' });
  const [adminEmails, setAdminEmails] = useState([]);
  const [savingCfg, setSavingCfg] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);

  const loadEmailCfg = async () => {
    try {
      const { data } = await api.get('/bus/admin/revenue-report-settings');
      setEmailCfg({ enabled: data.settings.enabled, recipients: (data.settings.recipients || []).join(', ') });
      setAdminEmails(data.adminEmails || []);
    } catch (e) { /* non-admin or error */ }
  };

  const saveEmailCfg = async () => {
    setSavingCfg(true);
    try {
      const recipients = emailCfg.recipients.split(',').map((e) => e.trim()).filter(Boolean);
      await api.put('/bus/admin/revenue-report-settings', { enabled: emailCfg.enabled, recipients });
      toast({ title: 'Saved', description: 'Monthly report settings updated.' });
    } catch (e) {
      toast({ title: 'Error', description: 'Could not save settings.', variant: 'destructive' });
    } finally {
      setSavingCfg(false);
    }
  };

  const sendNow = async () => {
    setSendingNow(true);
    try {
      const { data } = await api.post('/bus/admin/revenue-report/send');
      if (data.success) {
        toast({ title: 'Report sent', description: `Emailed to ${data.sent} recipient(s) for ${data.month}.` });
      } else if (data.error === 'no_recipients') {
        toast({ title: 'No recipients', description: 'Add at least one recipient first.', variant: 'destructive' });
      } else {
        toast({
          title: 'Send failed',
          description: 'Email provider rejected the send. Verify your email (Resend) API key in production.',
          variant: 'destructive'
        });
      }
    } catch (e) {
      toast({ title: 'Error', description: 'Could not send report.', variant: 'destructive' });
    } finally {
      setSendingNow(false);
    }
  };

  const load = async (r) => {
    setLoading(true);
    try {
      const [dRes, oRes, pRes] = await Promise.all([
        api.get('/bus/admin/fees-report', { params: { from: r.from, to: r.to, group_by: 'day' } }),
        api.get('/bus/admin/fees-report', { params: { from: r.from, to: r.to, group_by: 'operator' } }),
        api.get('/bus/admin/fees-report', { params: { from: monthStart(-1), to: monthEnd(-1), group_by: 'day' } }),
      ]);
      setDaily(dRes.data);
      setByOperator(oRes.data);
      setPrevFees(pRes.data?.totals?.platformFees ?? 0);
    } catch (e) {
      toast({ title: 'Failed to load revenue', description: 'Could not fetch fees report.', variant: 'destructive' });
      setDaily(null); setByOperator(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(range); loadEmailCfg(); /* eslint-disable-next-line */ }, []);

  const downloadCsv = async () => {
    setCsvLoading(true);
    try {
      const res = await api.get('/bus/admin/fees-report.csv', {
        params: { from: range.from, to: range.to, group_by: 'operator' },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `platform-revenue_${range.from}_${range.to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Download failed', description: 'Could not export CSV.', variant: 'destructive' });
    } finally {
      setCsvLoading(false);
    }
  };

  const totals = daily?.totals || {};
  const curFees = totals.platformFees ?? 0;
  const growth = (prevFees && prevFees > 0)
    ? ((curFees - prevFees) / prevFees) * 100
    : (curFees > 0 ? 100 : 0);
  const topOperators = [...(byOperator?.rows || [])]
    .sort((a, b) => b.platformFees - a.platformFees)
    .slice(0, 10);

  return (
    <div className="space-y-6" data-testid="platform-revenue-tab">
      {/* Controls */}
      <Card className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Banknote className="h-5 w-5 text-indigo-600" />
            <h2 className="font-semibold text-gray-900">Platform Revenue</h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="date" value={range.from} max={range.to}
              onChange={(e) => setRange({ ...range, from: e.target.value })}
              className="text-xs border border-gray-300 rounded-md px-2 py-1"
              data-testid="revenue-from-input"
            />
            <span className="text-xs text-gray-400">to</span>
            <input
              type="date" value={range.to} min={range.from} max={todayStr()}
              onChange={(e) => setRange({ ...range, to: e.target.value })}
              className="text-xs border border-gray-300 rounded-md px-2 py-1"
              data-testid="revenue-to-input"
            />
            <Button size="sm" variant="outline" className="h-8 text-xs" disabled={loading}
              onClick={() => load(range)} data-testid="revenue-apply-btn">
              {loading ? 'Loading…' : 'Apply'}
            </Button>
            <Button size="sm" className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700" disabled={csvLoading}
              onClick={downloadCsv} data-testid="revenue-csv-btn">
              <Download className="h-3.5 w-3.5 mr-1" />{csvLoading ? 'Exporting…' : 'CSV'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Platform Fees (your revenue)" accent="text-indigo-700" testid="revenue-platform-fees"
          value={`K${curFees.toLocaleString()}`}
          sub={
            <span className={growth >= 0 ? 'text-green-600' : 'text-red-600'}>
              {growth >= 0 ? '▲' : '▼'} {Math.abs(growth).toFixed(0)}% vs last month
            </span>
          } />
        <StatCard label="Gross Collected" testid="revenue-gross" value={`K${(totals.grossCollected ?? 0).toLocaleString()}`} />
        <StatCard label="Operator Payouts" accent="text-green-700" testid="revenue-operator" value={`K${(totals.operatorRevenue ?? 0).toLocaleString()}`} />
        <StatCard label="Seats Sold" testid="revenue-seats" value={totals.seats ?? 0} sub={`${totals.bookings ?? 0} bookings`} />
      </div>

      {/* Daily trend */}
      <Card className="p-5">
        <h3 className="font-semibold text-gray-900 mb-3">Daily Revenue Trend</h3>
        {daily?.rows?.length > 0 ? (
          <div style={{ width: '100%', height: 260 }} data-testid="revenue-daily-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[...daily.rows].sort((a, b) => a.key.localeCompare(b.key))} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2ff" vertical={false} />
                <XAxis dataKey="key" tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={(d) => (d || '').slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #c7d2fe' }}
                  formatter={(v, n) => [`K${Number(v).toLocaleString()}`, n]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="operatorRevenue" name="Operator Payouts" stackId="a" fill="#15803d" />
                <Bar dataKey="platformFees" name="Platform Fees" stackId="a" fill="#4f46e5" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-sm text-gray-400 py-8 text-center">No revenue in this period.</p>
        )}
      </Card>

      {/* Top operators */}
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-4 w-4 text-indigo-600" />
          <h3 className="font-semibold text-gray-900">Top Operators by Platform Fees</h3>
        </div>
        {topOperators.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="revenue-top-operators-table">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="py-2 pr-3">#</th>
                  <th className="py-2 pr-3">Operator</th>
                  <th className="py-2 pr-3 text-right">Bookings</th>
                  <th className="py-2 pr-3 text-right">Seats</th>
                  <th className="py-2 pr-3 text-right">Operator Payouts</th>
                  <th className="py-2 pr-3 text-right">Platform Fees</th>
                </tr>
              </thead>
              <tbody>
                {topOperators.map((r, i) => (
                  <tr key={r.operatorId || i} className="border-b last:border-0">
                    <td className="py-2 pr-3 text-gray-400">{i + 1}</td>
                    <td className="py-2 pr-3 font-medium text-gray-900">{r.operatorName || '—'}</td>
                    <td className="py-2 pr-3 text-right">{r.bookings}</td>
                    <td className="py-2 pr-3 text-right">{r.seats}</td>
                    <td className="py-2 pr-3 text-right text-green-700">K{r.operatorRevenue.toLocaleString()}</td>
                    <td className="py-2 pr-3 text-right font-semibold text-indigo-700">K{r.platformFees.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-400 py-8 text-center">No operator revenue in this period.</p>
        )}
      </Card>

      {/* Month-end email report settings */}
      <Card className="p-5" data-testid="revenue-email-settings">
        <div className="flex items-center gap-2 mb-3">
          <Mail className="h-4 w-4 text-indigo-600" />
          <h3 className="font-semibold text-gray-900">Automated Month-End Report</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          On the 1st of each month, Tekstar emails the previous month's revenue summary + CSV to the recipients below.
        </p>
        <div className="flex items-center gap-3 mb-4">
          <Switch
            checked={emailCfg.enabled}
            onCheckedChange={(v) => setEmailCfg({ ...emailCfg, enabled: v })}
            data-testid="revenue-email-enabled-switch"
          />
          <span className="text-sm text-gray-700">{emailCfg.enabled ? 'Enabled' : 'Disabled'}</span>
        </div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Recipients (comma-separated)</label>
        <Input
          value={emailCfg.recipients}
          onChange={(e) => setEmailCfg({ ...emailCfg, recipients: e.target.value })}
          placeholder="finance@yourco.com, ceo@yourco.com"
          className="mb-2"
          data-testid="revenue-email-recipients-input"
        />
        {adminEmails.length > 0 && (
          <p className="text-[11px] text-gray-400 mb-3">
            Admins always receive it: {adminEmails.join(', ')}
          </p>
        )}
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={saveEmailCfg} disabled={savingCfg}
            className="bg-indigo-600 hover:bg-indigo-700" data-testid="revenue-email-save-btn">
            {savingCfg ? 'Saving…' : 'Save'}
          </Button>
          <Button size="sm" variant="outline" onClick={sendNow} disabled={sendingNow}
            data-testid="revenue-email-sendnow-btn">
            <Send className="h-3.5 w-3.5 mr-1" />{sendingNow ? 'Sending…' : 'Send last month now'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default PlatformRevenueTab;
